/* eslint-disable react-hooks/exhaustive-deps */
import CancelIcon from "@material-ui/icons/Cancel";
import { useContext, useMemo, useState } from "react";
import { useHistory } from "react-router";
import AuthApi from "../../context/AuthApi";
import useDocumentTitle from "../../utils/useDocumentTitle";

import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import { useTranslation } from "react-i18next";
import CampaignFilter from "../../components/CampaignFilter";
import DataTable from "../../components/DataTable";
import GetAllCountries from "../../components/GetAllCountries";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { SessionStatus } from "../../config/Config";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api";
import { formatLocalDate, formattedDateString } from "../../utils/datetime";
import { getQueryVariable } from "../../utils/getQueryParam";
import styles from "./Logs.module.scss";
import { LogsFilterBarV2 } from "./LogsFilterBarV2";
import { loadFilterState } from "./useLogsFilters";

dayjs.extend(utc);

const STORAGE_KEY = "logsSupport_filterState";

async function getSupportAdminSessionLogs({
  campaignId,
  countryId,
  page,
  limit,
  columnName,
  order,
  startDate,
  endDate,
  type,
  subject,
  coach,
  coachee,
  status,
  searchKey
}) {
  const { data } = await apiRequest.get("/api/getSupportAdminSessionLogs", {
    params: {
      page,
      limit,
      columnName: columnName,
      order,
      campaign_id: campaignId,
      country_id: countryId,
      start_date: startDate,
      end_date: endDate,
      type,
      subject,
      coach: coach,
      coachee: coachee,
      status,
      search: searchKey
    },
  });
  return data;
}

const columns = [
  {
    id: "session_datetime",
    label: "Start Date",
    sortable: true,
    render: (val) => (
      <span>{formatLocalDate(val)}</span>
    ),
  },
  {
    id: "id",
    label: "Session ID",
    sortable: false,
  },
  {
    id: "session_duration",
    label: "Duration",
    sortable: false,
  },
  {
    id: "subject",
    label: "Subject",
    sortable: false,
  },
  {
    id: "u_fullname",
    label: "Name",
    sortable: false,
  },
  {
    id: "coachee_employee_id",
    label: "Employee ID",
    sortable: false,
  },
  {
    id: "completion_datetime",
    label: "Completion Date",
    sortable: false,
    render: (val) => (val ? formatLocalDate(val) : "—"),
  },
  {
    id: "status",
    label: "Status",
    sortable: false,
    render: (_, row) => {
      if (row.framework !== 1) return <span>-</span>;
      if (row.status === SessionStatus.INCOMPLETE) return <span className={styles.statusExpired}>Expired</span>;
      const isPassed = row.overall_status === "Passed";
      return (
        <span className={isPassed ? styles.statusPassed : styles.statusFailed}>
          {row.overall_status || "-"}
        </span>
      );
    },
  },
];

export function LogsSupport(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const Auth = useContext(AuthApi);
  const [filterValues, setFilterValues] = useState(() => ({
    ...loadFilterState(STORAGE_KEY),
    showFilter: true,
  }));
  const {
    selectedDateRange,
    selectedType,
    selectedSubject,
    selectedCoachNames,
    selectedNames,
    selectedStatus,
    searchKey,
  } = filterValues;
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [id, setId] = useState(getQueryVariable("id"));

  const filters = useMemo(
    () => ({
      startDate: formattedDateString(selectedDateRange?.startDate),
      endDate: formattedDateString(selectedDateRange?.endDate),
      type: selectedType?.type ?? null,
      subject: selectedSubject?.id ?? null,
      coach: selectedCoachNames?.uid ?? null,
      coachee: selectedNames?.uid ?? null,
      status: selectedStatus?.value ?? null,
      countryId: selectedCountry,
      campaignId: selectedCampaign,
      searchKey: searchKey ?? ""
    }),
    [selectedDateRange, selectedType, selectedSubject, selectedCoachNames, selectedNames, selectedStatus, selectedCountry, selectedCampaign, searchKey]
  );

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>Logs</SectionTitle>

            <div className={styles.toolBar}>
              <GetAllCountries
                countryChange={val => {
                  setSelectedCountry(val);
                }}
              />
              <CampaignFilter
                handleCampaignChange={val => {
                  setSelectedCampaign(val);
                }}
              />
            </div>

            {id && (
              <div className={`${styles.status} ${styles.tag}`}>
                {getQueryVariable("name")}
                <CancelIcon
                  onClick={() => {
                    setId("");
                    history.push("/logs/team");
                  }}
                />
              </div>
            )}

            <LogsFilterBarV2
              storageKey={STORAGE_KEY}
              onChange={setFilterValues}
              selectedCampaign={selectedCampaign}
              selectedCountry={selectedCountry}
              dateRange
              type
              subject
              coach
              coachee
              status
            />

            {selectedCountry && (
              <DataTable
                columns={columns}
                fetchData={getSupportAdminSessionLogs}
                filters={filters}
                defaultOrderBy="session_datetime"
                defaultOrder="desc"
                onRowClick={true}
                storageKey="logsSupport"
              />
            )}
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
